/**
 * /transit/people — the org's accounts, for its admins: invite someone (a
 * single-use link to send them; there's no email yet), change a role, disable
 * an account (signed out at once), or make a reset link for a lost password.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Navigate } from 'react-router-dom'
import { Check, Copy } from 'lucide-react'
import { Button, Input } from '../../components/ui'
import { useTransitSession } from '../../components/transit/session'
import { timeAgo } from '../../components/transit/status'
import { transitApi } from '../../services/transit'
import type { Invite, TransitRole, TransitUser } from '../../types/transit'

const select =
  'h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50'

/** A link just made, shown once: only its hash is stored. */
function LinkBox({ label, token, onDone }: { label: string; token: string; onDone: () => void }) {
  const url = `${window.location.origin}/transit/invite/${token}`
  const [copied, setCopied] = useState(false)
  return (
    <div className="space-y-2 rounded-lg border border-border bg-muted/50 p-3">
      <p className="text-sm text-foreground">{label} Send it to them yourself; it works once, for 72 hours, and isn't shown again.</p>
      <div className="flex gap-2">
        <input readOnly value={url} onFocus={(e) => e.target.select()} className="h-8 min-w-0 flex-1 rounded-md border border-border bg-background px-2 font-mono text-xs" />
        <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(url).then(() => setCopied(true))}>
          {copied ? <Check /> : <Copy />} {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>Done</Button>
      </div>
    </div>
  )
}

export default function TransitPeople() {
  const { user: me } = useTransitSession()
  const [users, setUsers] = useState<TransitUser[]>([])
  const [invites, setInvites] = useState<Invite[]>([])
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<TransitRole>('member')
  const [link, setLink] = useState<{ label: string; token: string } | null>(null)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      const [u, i] = await Promise.all([transitApi.listUsers(), transitApi.listInvites()])
      setUsers(u)
      setInvites(i)
    } catch (e: any) {
      setError(e.message)
    }
  }, [])
  useEffect(() => { load() }, [load])

  if (me.role !== 'admin' && me.role !== 'owner') return <Navigate to="/transit" replace />

  const run = async (fn: () => Promise<unknown>) => {
    setError('')
    try {
      await fn()
      await load()
    } catch (e: any) {
      setError(e.message)
    }
  }

  const invite = (e: FormEvent) => {
    e.preventDefault()
    run(async () => {
      const r = await transitApi.createInvite(email, role)
      setLink({ label: `Invite link for ${r.invite.email}.`, token: r.token })
      setEmail('')
    })
  }

  return (
    <div className="mx-auto max-w-4xl space-y-8 px-6 py-10">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">People</h1>
        <p className="text-sm text-muted-foreground">Everyone who can sign in to your org's transitions.</p>
      </div>
      {error && <div role="alert" className="text-sm text-destructive">{error}</div>}
      {link && <LinkBox label={link.label} token={link.token} onDone={() => setLink(null)} />}

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-foreground">Invite someone</h2>
        <form onSubmit={invite} className="flex flex-wrap items-end gap-2">
          <div className="min-w-64 flex-1">
            <Input id="invite-email" label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <select aria-label="Role" value={role} onChange={(e) => setRole(e.target.value as TransitRole)} className={`${select} h-9`}>
            <option value="member">Member</option>
            <option value="owner">Owner</option>
            {me.role === 'admin' && <option value="admin">Admin</option>}
          </select>
          <Button type="submit">Make invite link</Button>
        </form>
        {invites.length > 0 && (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {invites.map((i) => (
              <li key={i.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="flex-1 truncate">{i.email}</span>
                <span className="text-xs text-muted-foreground">
                  {i.kind === 'reset' ? 'password reset' : i.role}, expires {new Date(i.expires_at).toLocaleString()}
                </span>
                <Button size="sm" variant="ghost" onClick={() => run(() => transitApi.revokeInvite(i.id))}>Revoke</Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-foreground">Accounts</h2>
        <ul className="divide-y divide-border rounded-lg border border-border">
          {users.map((u) => {
            const disabled = u.disabled_at !== null
            return (
              <li key={u.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
                <div className="min-w-0 flex-1">
                  <div className={`truncate font-medium ${disabled ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                    {u.name || u.email} {u.id === me.id && <span className="text-xs font-normal text-muted-foreground">(you)</span>}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {u.email} · {disabled ? 'disabled' : u.last_login_at ? `signed in ${timeAgo(u.last_login_at)}` : 'never signed in'}
                  </div>
                </div>
                <select
                  aria-label={`Role for ${u.email}`}
                  value={u.role}
                  onChange={(e) => run(() => transitApi.updateUser(u.id, e.target.value as TransitRole, disabled))}
                  disabled={u.id === me.id || (u.role === 'admin' && me.role !== 'admin')}
                  className={select}
                >
                  <option value="member">Member</option>
                  <option value="owner">Owner</option>
                  {(me.role === 'admin' || u.role === 'admin') && <option value="admin">Admin</option>}
                </select>
                <Button size="sm" variant="outline"
                  onClick={() => run(async () => {
                    const r = await transitApi.resetLink(u.id)
                    setLink({ label: `Password reset link for ${u.email}.`, token: r.token })
                  })}>
                  Reset link
                </Button>
                {u.id !== me.id && (
                  <Button size="sm" variant={disabled ? 'outline' : 'ghost'} onClick={() => run(() => transitApi.updateUser(u.id, u.role, !disabled))}>
                    {disabled ? 'Enable' : 'Disable'}
                  </Button>
                )}
              </li>
            )
          })}
        </ul>
      </section>
    </div>
  )
}
